import React, { useState } from 'react';
import axios from 'axios';
import { X, CloudUpload, CheckCircle, Loader2 } from 'lucide-react';

const ExternalApplyModal = ({ job, onClose }) => {
  const [parsing, setParsing] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    fullName: '', email: '', phone: '', citizenship: '',
    collegeName: '', currentCGPA: '', tenth: '', twelfth: '',
    totalExperience: '', relevantExperience: '', currentCTC: '', expectedCTC: '',
    skills: '', noticePeriod: '', isServingNotice: 'false', linkedinURL: ''
  });

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setResumeFile(file);
    setParsing(true);

    const data = new FormData();
    data.append('resume', file);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/parse-resume`, data);
      const { fullName, email, phone, collegeName } = response.data;
      
      setFormData(prev => ({
        ...prev,
        fullName: fullName || prev.fullName,
        email: email || prev.email,
        phone: phone || prev.phone,
        collegeName: collegeName || prev.collegeName
      }));
    } catch (error) {
      console.error("Resume parsing failed", error);
    } finally {
      setParsing(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!resumeFile) return alert("Please upload your resume PDF.");

    const applicantId = localStorage.getItem('applicantId');
    if (!applicantId) {
        alert("Session expired. Please log in again.");
        return;
    }

    setSubmitting(true);
    const data = new FormData();
    data.append('jobId', job._id || job.id); 
    data.append('publicApplicantId', applicantId); 
    data.append('resume', resumeFile);
    
    Object.keys(formData).forEach(key => {
        data.append(key, formData[key]);
    });

    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/apply-external`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert("Application Submitted Successfully!");
      onClose(); 
    } catch (error) {
      console.error("Application error:", error);
      
      if (error.response?.data?.requiresUpgrade) {
          alert("PREMIUM WALL: You have 0 Premium Credits. Please click the 'Buy Credits' button on the dashboard to apply for Premium Jobs!");
          onClose();
      } else {
          const errorMsg = error.response?.data?.message || "Failed to submit application.";
          alert(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const overlayStyle = { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(5px)', zIndex: 100000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' };
  const modalStyle = { backgroundColor: '#fff', width: '100%', maxWidth: '750px', maxHeight: '90vh', borderRadius: '16px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)', display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: 'slideUp 0.3s ease-out' };
  const formSectionStyle = { padding: '24px 32px', borderBottom: '1px solid #f1f5f9' };
  const labelStyle = { display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#334155', marginBottom: '6px' };
  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', color: '#0f172a', outline: 'none', transition: 'border-color 0.2s' };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        
        <div style={{ padding: '20px 32px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
           <div>
             <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#0f172a' }}>Apply for {job.role}</h2>
             <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.9rem', fontWeight: '500' }}>at {job.companyName}</p>
           </div>
           <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
             <X size={24} />
           </button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
            <form onSubmit={handleSubmit}>
              
              <div style={formSectionStyle}>
                <h4 style={{ margin: '0 0 16px', color: '#0f172a', fontSize: '1rem' }}>1. Upload Resume (Auto-fill)</h4>
                <div style={{ position: 'relative' }}>
                    <input type="file" accept=".pdf" id="resume-upload" onChange={handleFileChange} hidden />
                    <label htmlFor="resume-upload" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '24px', border: '2px dashed #cbd5e1', borderRadius: '12px', cursor: 'pointer', background: '#f8fafc', transition: 'background 0.2s' }}>
                        {parsing ? <Loader2 className="animate-spin" color="#3b82f6" /> : resumeFile ? <CheckCircle color="#10b981" /> : <CloudUpload color="#64748b" />}
                        <span style={{ fontWeight: '600', color: '#334155' }}>
                            {parsing ? "Parsing Resume..." : resumeFile ? resumeFile.name : "Click to Upload PDF Resume"}
                        </span>
                    </label>
                </div>
                {resumeFile && !parsing && (
                    <p style={{ color: '#10b981', fontSize: '0.85rem', marginTop: '8px', fontWeight: '600' }}>
                        ✓ Resume parsed successfully! Verify details below.
                    </p>
                )}
              </div>

              <div style={formSectionStyle}>
                <h4 style={{ margin: '0 0 16px', color: '#0f172a', fontSize: '1rem' }}>2. Personal Details</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div><label style={labelStyle}>Full Name</label><input type="text" name="fullName" value={formData.fullName} onChange={handleChange} required placeholder="John Doe" style={inputStyle} /></div>
                    <div><label style={labelStyle}>Email</label><input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="john@example.com" style={inputStyle} /></div>
                    <div><label style={labelStyle}>Phone</label><input type="text" name="phone" value={formData.phone} onChange={handleChange} required placeholder="+91..." style={inputStyle} /></div>
                    <div><label style={labelStyle}>Citizenship</label><input type="text" name="citizenship" value={formData.citizenship} onChange={handleChange} required placeholder="Indian" style={inputStyle} /></div>
                </div>
              </div>

              <div style={formSectionStyle}>
                <h4 style={{ margin: '0 0 16px', color: '#0f172a', fontSize: '1rem' }}>3. Education</h4>
                <div style={{ marginBottom: '16px' }}><label style={labelStyle}>College Name</label><input type="text" name="collegeName" value={formData.collegeName} onChange={handleChange} required placeholder="University Name" style={inputStyle} /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                    <div><label style={labelStyle}>Current CGPA</label><input type="text" name="currentCGPA" value={formData.currentCGPA} onChange={handleChange} placeholder="8.5" style={inputStyle} /></div>
                    <div><label style={labelStyle}>10th %</label><input type="text" name="tenth" value={formData.tenth} onChange={handleChange} placeholder="85%" style={inputStyle} /></div>
                    <div><label style={labelStyle}>12th %</label><input type="text" name="twelfth" value={formData.twelfth} onChange={handleChange} placeholder="88%" style={inputStyle} /></div>
                </div>
              </div>

              <div style={formSectionStyle}>
                 <h4 style={{ margin: '0 0 16px', color: '#0f172a', fontSize: '1rem' }}>4. Experience & Skills</h4>
                 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div><label style={labelStyle}>Total Exp (Yrs)</label><input type="text" name="totalExperience" value={formData.totalExperience} onChange={handleChange} placeholder="2" style={inputStyle} /></div>
                    <div><label style={labelStyle}>Relevant Exp</label><input type="text" name="relevantExperience" value={formData.relevantExperience} onChange={handleChange} placeholder="1.5" style={inputStyle} /></div>
                    <div><label style={labelStyle}>Current CTC (LPA)</label><input type="text" name="currentCTC" value={formData.currentCTC} onChange={handleChange} placeholder="4.5" style={inputStyle} /></div>
                    <div><label style={labelStyle}>Expected CTC</label><input type="text" name="expectedCTC" value={formData.expectedCTC} onChange={handleChange} placeholder="6.0" style={inputStyle} /></div>
                 </div>
                 
                 <div style={{ marginBottom: '16px' }}><label style={labelStyle}>Skills</label><input type="text" name="skills" value={formData.skills} onChange={handleChange} placeholder="React, Node, Communication" style={inputStyle} /></div>
                 
                 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div><label style={labelStyle}>Notice Period</label><input type="text" name="noticePeriod" value={formData.noticePeriod} onChange={handleChange} placeholder="30 Days" style={inputStyle} /></div>
                    <div>
                        <label style={labelStyle}>Serving Notice?</label>
                        <select name="isServingNotice" value={formData.isServingNotice} onChange={handleChange} style={inputStyle}>
                            <option value="false">No</option>
                            <option value="true">Yes</option>
                        </select>
                    </div>
                 </div>
                 
                 <div><label style={labelStyle}>LinkedIn URL</label><input type="text" name="linkedinURL" value={formData.linkedinURL} onChange={handleChange} placeholder="https://linkedin.com/in/..." style={inputStyle} /></div>
              </div>

              <div style={{ padding: '24px 32px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', textAlign: 'right' }}>
                <button 
                    type="submit" 
                    disabled={submitting || parsing}
                    style={{
                        padding: '14px 40px',
                        backgroundColor: (submitting || parsing) ? '#94a3b8' : '#0f172a',
                        color: 'white', border: 'none', borderRadius: '10px',
                        fontSize: '1rem', fontWeight: '600', cursor: (submitting || parsing) ? 'not-allowed' : 'pointer'
                    }}
                >
                    {submitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>

            </form>
        </div>
      </div>
    </div>
  );
};

export default ExternalApplyModal;